// Drawing an employee card onto a canvas.
//
// The card artwork is the store's own uploaded image; this only paints the
// parts that change per person on top of it. Kept apart from the UI so the
// preview on screen and the file that gets exported are produced by exactly the
// same code — a preview drawn by different code than the export is a preview
// that lies.

// CR80 is 85.60 x 53.98 mm. Portrait, at 300 dpi (what an Evolis Primacy 2
// prints at), that is 638 x 1011 px. Everything else is derived from these, so
// exporting at 600 dpi is the same drawing at double the pixels.
export const CR80 = {
  widthMm: 53.98,
  heightMm: 85.60,
  pxAt: (dpi) => ({
    width: Math.round((53.98 / 25.4) * dpi),
    height: Math.round((85.60 / 25.4) * dpi),
  }),
};

const DEFAULT_TERMS = `Identification: Employees are required to keep their ID badge visible or easily accessible during working hours to confirm identity when needed.

Proper Use: The ID badge is issued solely for company related activities. It may not be lent, duplicated, or used for any non-official purpose.

Security: If the badge is misplaced or suspected to be compromised, report it immediately so access can be disabled.`;

// Positions are fractions of the card, never pixels, so one saved layout is
// correct at any export resolution and on the on-screen preview alike.
// x/y are the CENTRE of each element — except the terms block, which is
// anchored at its top so it grows downwards as the text gets longer.
export const DEFAULT_LAYOUT = {
  // Not an element you can drag — how the uploaded artwork sits on the card.
  // 'cover' fills and crops, 'contain' shows all of it, 'stretch' distorts.
  artwork: { fit: 'cover' },
  logo: { show: false, x: 0.5, y: 0.075, size: 0.34 },
  photo: { show: true, x: 0.5, y: 0.315, size: 0.50, radius: 0.20, ring: 0.014, ringColor: '#1668dc' },
  name: { show: true, x: 0.5, y: 0.565, size: 0.082, color: '#0b1324', weight: 800, upper: true },
  role: { show: true, x: 0.5, y: 0.615, size: 0.042, color: '#334155', weight: 700, upper: true, track: 0.14 },
  id: { show: true, x: 0.5, y: 0.678, size: 0.040, color: '#0b1324', label: 'No. ID' },
  phone: { show: true, x: 0.5, y: 0.722, size: 0.040, color: '#0b1324', label: 'Phone' },
  qr: { show: true, x: 0.5, y: 0.855, size: 0.27, quiet: 0.05 },

  // The reverse of the card: the store's mark, the rules, and the ID number
  // again so a found badge can be handed back without turning it over.
  back: {
    logo: { show: true, x: 0.5, y: 0.085, size: 0.30 },
    terms: {
      show: true, x: 0.5, y: 0.22, size: 0.036, width: 0.82, align: 'left',
      color: '#0b1324', lineHeight: 1.45, title: 'TERMS & CONDITIONS',
      titleColor: '#0b1324', text: DEFAULT_TERMS,
    },
    id: { show: true, x: 0.5, y: 0.93, size: 0.036, color: '#0b1324', label: 'No. ID' },
  },
};

// The back's elements live under `back`, so it needs its own merge pass rather
// than the shallow one the front gets.
export const mergeLayout = (saved) => {
  const out = {};
  for (const key of Object.keys(DEFAULT_LAYOUT)) {
    if (key === 'back') continue;
    out[key] = { ...DEFAULT_LAYOUT[key], ...(saved?.[key] || {}) };
  }
  out.back = {};
  for (const key of Object.keys(DEFAULT_LAYOUT.back)) {
    out.back[key] = { ...DEFAULT_LAYOUT.back[key], ...(saved?.back?.[key] || {}) };
  }
  return out;
};

/** Which element keys each side owns, in stacking order. */
export const SIDE_KEYS = {
  front: ['logo', 'photo', 'name', 'role', 'id', 'phone', 'qr'],
  back: ['logo', 'terms', 'id'],
};

/** The layout block for one side. */
export const sideLayout = (layout, side) => (side === 'back' ? (layout.back || {}) : layout);

// Browsers cache decoded images per src, but an <img> still has to load before
// it can be drawn — and drawing a half-loaded image silently paints nothing.
export const loadImage = (src) => new Promise((resolve) => {
  if (!src) return resolve(null);
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
  return undefined;
});

const roundedRectPath = (ctx, x, y, w, h, r) => {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
};

// Canvas has no letter-spacing before recent Chrome, and the role line reads
// wrong without it, so it is drawn a glyph at a time when tracking is asked for.
const drawTracked = (ctx, text, cx, cy, track) => {
  const chars = [...text];
  const widths = chars.map((c) => ctx.measureText(c).width);
  const total = widths.reduce((s, w) => s + w, 0) + track * (chars.length - 1);
  let x = cx - total / 2;
  ctx.textAlign = 'left';
  chars.forEach((c, i) => {
    ctx.fillText(c, x, cy);
    x += widths[i] + track;
  });
  ctx.textAlign = 'center';
  return total;
};

// The store's mark. Size is its width as a fraction of the card; the height
// follows the image so a wordmark never gets squashed into a square.
const drawLogo = (ctx, cfg, img, { width, height }, boxes) => {
  if (!img) return;
  const w = cfg.size * width;
  const h = img.width ? w * (img.height / img.width) : w;
  const x = cfg.x * width - w / 2;
  const y = cfg.y * height - h / 2;
  ctx.drawImage(img, x, y, w, h);
  boxes.logo = { x, y, w, h };
};

// The detail rows line up on the colon, the way the printed card does — a
// centred "label : value" wanders as values change length.
const drawRow = (ctx, key, cfg, value, { width, height }, boxes) => {
  if (!value) return;
  const fontPx = cfg.size * width;
  ctx.font = `700 ${fontPx}px Arial, sans-serif`;
  const label = `${cfg.label || ''} :`;
  const labelW = ctx.measureText(label).width;
  const colonX = cfg.x * width;
  const y = cfg.y * height;
  ctx.fillStyle = cfg.color;
  ctx.textAlign = 'right';
  ctx.fillText(label, colonX, y);
  ctx.textAlign = 'left';
  ctx.font = `400 ${fontPx}px Arial, sans-serif`;
  const gap = fontPx * 0.35;
  ctx.fillText(String(value), colonX + gap, y);
  ctx.textAlign = 'center';
  const valueW = ctx.measureText(String(value)).width;
  boxes[key] = {
    x: colonX - labelW, y: y - fontPx * 0.6, w: labelW + gap + valueW, h: fontPx * 1.2,
  };
};

// A paragraph that opens with "Something:" gets that lead-in in bold, the way
// a printed terms block reads. Everything after it is regular weight, and both
// weights wrap as one stream of words.
const tokenizeParagraph = (para) => {
  const lead = para.match(/^([^:\n]{1,48}:)\s*/);
  const rest = lead ? para.slice(lead[0].length) : para;
  const tokens = lead
    ? lead[1].split(/\s+/).filter(Boolean).map((w) => ({ w, bold: true }))
    : [];
  return tokens.concat(rest.split(/\s+/).filter(Boolean).map((w) => ({ w, bold: false })));
};

const fontFor = (fontPx, bold) => `${bold ? 700 : 400} ${fontPx}px Arial, sans-serif`;

const wrapTokens = (ctx, tokens, fontPx, maxW) => {
  const lines = [];
  let line = [];
  let lineW = 0;
  for (const tok of tokens) {
    ctx.font = fontFor(fontPx, tok.bold);
    const w = ctx.measureText(tok.w).width;
    const space = line.length ? ctx.measureText(' ').width : 0;
    if (line.length && lineW + space + w > maxW) {
      lines.push({ tokens: line, width: lineW });
      line = [{ ...tok, w: tok.w, width: w }];
      lineW = w;
    } else {
      line.push({ ...tok, width: w, space });
      lineW += space + w;
    }
  }
  if (line.length) lines.push({ tokens: line, width: lineW });
  return lines;
};

const drawTerms = (ctx, cfg, { width, height }, boxes) => {
  const fontPx = cfg.size * width;
  const blockW = (cfg.width || 0.82) * width;
  const lineH = (cfg.lineHeight || 1.5) * fontPx;
  const left = cfg.x * width - blockW / 2;
  const top = cfg.y * height;
  const centred = cfg.align === 'center';
  let y = top;

  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';

  if (cfg.title) {
    const titlePx = fontPx * 1.2;
    ctx.font = `800 ${titlePx}px Arial, sans-serif`;
    ctx.fillStyle = cfg.titleColor || cfg.color;
    const titleW = ctx.measureText(cfg.title).width;
    ctx.fillText(cfg.title, centred ? cfg.x * width - titleW / 2 : left, y);
    y += titlePx * 1.9;
  }

  ctx.fillStyle = cfg.color;
  for (const para of String(cfg.text || '').split('\n')) {
    if (!para.trim()) { y += lineH * 0.6; continue; }
    const lines = wrapTokens(ctx, tokenizeParagraph(para), fontPx, blockW);
    for (const line of lines) {
      let x = centred ? cfg.x * width - line.width / 2 : left;
      for (const tok of line.tokens) {
        x += tok.space || 0;
        ctx.font = fontFor(fontPx, tok.bold);
        ctx.fillText(tok.w, x, y);
        x += tok.width;
      }
      y += lineH;
    }
    y += lineH * 0.35;   // breathing room between paragraphs
  }

  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  boxes.terms = { x: left, y: top, w: blockW, h: Math.max(y - top, fontPx) };
};

/**
 * Paint one side of a card.
 *
 * Returns the pixel rectangle each element ended up occupying, keyed by layout
 * key — the editor drags what it can see, and only the drawing code knows how
 * wide a name actually rendered.
 *
 * @param ctx      2d context sized to width x height
 * @param opts.employee   { name, role, employeeId, phone, photo, qr } (data URIs)
 * @param opts.images     { template, photo, qr, logo } already-loaded HTMLImageElements
 * @param opts.layout     merged layout
 * @param opts.side       'front' | 'back'
 */
export function drawBadge(ctx, { width, height, images, employee, layout, side = 'front' }) {
  const L = layout;
  const geo = { width, height };
  const boxes = {};
  ctx.clearRect(0, 0, width, height);

  // The card stock is white; without this an uploaded PNG with transparency
  // exports with a black background on some printers.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);

  if (images.template) {
    const img = images.template;
    const fit = L.artwork?.fit || 'cover';
    if (fit === 'stretch') {
      ctx.drawImage(img, 0, 0, width, height);
    } else {
      // cover fills the card and crops the overflow; contain keeps the whole
      // design on the card and leaves white where the proportions differ.
      const scale = fit === 'contain'
        ? Math.min(width / img.width, height / img.height)
        : Math.max(width / img.width, height / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (width - w) / 2, (height - h) / 2, w, h);
    }
  }

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  if (side === 'back') {
    const B = L.back || {};
    if (B.logo?.show) drawLogo(ctx, B.logo, images.logo, geo, boxes);
    if (B.terms?.show) drawTerms(ctx, B.terms, geo, boxes);
    if (B.id?.show) drawRow(ctx, 'id', B.id, employee.employeeId, geo, boxes);
    return boxes;
  }

  if (L.logo.show) drawLogo(ctx, L.logo, images.logo, geo, boxes);

  if (L.photo.show) {
    const size = L.photo.size * width;
    const x = L.photo.x * width - size / 2;
    const y = L.photo.y * height - size / 2;
    const r = L.photo.radius * size;
    const ring = L.photo.ring * width;

    if (ring > 0) {
      ctx.save();
      roundedRectPath(ctx, x - ring, y - ring, size + ring * 2, size + ring * 2, r + ring);
      ctx.fillStyle = L.photo.ringColor || '#1668dc';
      ctx.fill();
      ctx.restore();
    }

    ctx.save();
    roundedRectPath(ctx, x, y, size, size, r);
    ctx.clip();
    if (images.photo) {
      // cover-crop the portrait into the frame rather than squashing a face
      const img = images.photo;
      const s = Math.max(size / img.width, size / img.height);
      const w = img.width * s;
      const h = img.height * s;
      ctx.drawImage(img, x + (size - w) / 2, y + (size - h) / 2, w, h);
    } else {
      ctx.fillStyle = '#e2e8f0';
      ctx.fillRect(x, y, size, size);
      ctx.fillStyle = '#64748b';
      ctx.font = `800 ${size * 0.38}px Arial, sans-serif`;
      const initials = (employee.name || '?').split(/\s+/).map((w) => w[0]).filter(Boolean)
        .slice(0, 2).join('').toUpperCase() || '?';
      ctx.fillText(initials, x + size / 2, y + size / 2);
    }
    ctx.restore();
    boxes.photo = { x: x - ring, y: y - ring, w: size + ring * 2, h: size + ring * 2 };
  }

  if (L.name.show && employee.name) {
    const text = L.name.upper ? employee.name.toUpperCase() : employee.name;
    ctx.fillStyle = L.name.color;
    // Shrink to fit rather than spill off the card — some names are long.
    let fontPx = L.name.size * width;
    const maxW = width * 0.88;
    do {
      ctx.font = `${L.name.weight} ${fontPx}px Arial, sans-serif`;
      if (ctx.measureText(text).width <= maxW) break;
      fontPx -= Math.max(1, fontPx * 0.04);
    } while (fontPx > width * 0.03);
    const cx = L.name.x * width;
    const cy = L.name.y * height;
    ctx.fillText(text, cx, cy);
    const w = ctx.measureText(text).width;
    boxes.name = { x: cx - w / 2, y: cy - fontPx * 0.6, w, h: fontPx * 1.2 };
  }

  if (L.role.show && employee.role) {
    const text = L.role.upper ? employee.role.toUpperCase() : employee.role;
    const fontPx = L.role.size * width;
    const cx = L.role.x * width;
    const cy = L.role.y * height;
    ctx.fillStyle = L.role.color;
    ctx.font = `${L.role.weight} ${fontPx}px Arial, sans-serif`;
    const w = drawTracked(ctx, text, cx, cy, (L.role.track || 0) * fontPx);
    boxes.role = { x: cx - w / 2, y: cy - fontPx * 0.6, w, h: fontPx * 1.2 };
  }

  if (L.id.show) drawRow(ctx, 'id', L.id, employee.employeeId, geo, boxes);
  if (L.phone.show) drawRow(ctx, 'phone', L.phone, employee.phone, geo, boxes);

  if (L.qr.show && images.qr) {
    const size = L.qr.size * width;
    const x = L.qr.x * width - size / 2;
    const y = L.qr.y * height - size / 2;
    const quiet = (L.qr.quiet || 0) * size;
    boxes.qr = { x: x - quiet, y: y - quiet, w: size + quiet * 2, h: size + quiet * 2 };
    // A white quiet zone behind the code: scanners need the margin, and the
    // store's artwork underneath is usually neither white nor plain.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x - quiet, y - quiet, size + quiet * 2, size + quiet * 2);
    ctx.imageSmoothingEnabled = false;   // keep the modules crisp
    ctx.drawImage(images.qr, x, y, size, size);
    ctx.imageSmoothingEnabled = true;
  }

  return boxes;
}

/** Load every image one card needs, in parallel. */
export async function loadBadgeImages({ employee, template, logo }) {
  const [tpl, photo, qr, mark] = await Promise.all([
    loadImage(template),
    loadImage(employee?.photo),
    loadImage(employee?.qr),
    loadImage(logo),
  ]);
  return { template: tpl, photo, qr, logo: mark };
}

/** Render one side to a PNG blob at the given dpi. */
export async function renderBadgePng({ employee, template, logo, layout, side = 'front', dpi = 300 }) {
  const { width, height } = CR80.pxAt(dpi);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const images = await loadBadgeImages({ employee, template, logo });
  drawBadge(canvas.getContext('2d'), { width, height, images, employee, layout, side });
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
}
